/**
 * The log's Home Screen step: how a parent who uses Babia in a browser tab on
 * a phone or tablet adds it to the Home Screen. On iPhone and iPad, bottle
 * alerts work only from there (lib/push-state.ts). Pure, so the user agents
 * are unit-tested; the card is components/log/home-screen-step.tsx.
 */
import { type MetaApp, metaInAppBrowser } from "./in-app-browser.ts";

export type HomeScreenStep =
  /**
   * Safari on iPhone or iPad: Share, then Add to Home Screen. Brave, Orion,
   * and an app's Safari sheet look the same and add the same way.
   */
  | { kind: "safari" }
  /** Chrome, Edge, or Firefox on iPhone or iPad: their own Share button. */
  | { kind: "ios-browser" }
  /**
   * An app's own browser (Instagram, Facebook, TikTok…), which cannot add a
   * page to the Home Screen: open the page in a browser first. `app` names a
   * Meta app; another app's browser is null.
   */
  | { kind: "in-app"; os: "ios" | "android"; app: MetaApp | null }
  /** Android with the browser's install prompt saved: an Install button. */
  | { kind: "install" }
  /** Android without it: the browser's menu, Chrome's or another's. */
  | { kind: "android-menu"; chrome: boolean };

export type HomeScreenKind = HomeScreenStep["kind"];

export interface HomeScreenDevice {
  userAgent: string;
  /** iPadOS reports itself as a Mac; touch tells them apart. */
  maxTouchPoints: number;
  /** Babia is open from the Home Screen already (analytics' `displayMode`). */
  standalone: boolean;
  /** A `beforeinstallprompt` event is saved (lib/install-prompt.ts). */
  installPrompt: boolean;
}

/** The step for this device, or null: the Home Screen app, a desktop, or a laptop. */
export function homeScreenStep({
  userAgent,
  maxTouchPoints,
  standalone,
  installPrompt,
}: HomeScreenDevice): HomeScreenStep | null {
  if (standalone) return null;
  const ios =
    /iP(hone|ad|od)/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
  const android = /Android/.test(userAgent);
  if (!ios && !android) return null;
  const os = ios ? "ios" : "android";
  const app = metaInAppBrowser(userAgent);
  if (app) return { kind: "in-app", os, app };
  if (ios) {
    if (/\b(CriOS|EdgiOS|FxiOS)\//.test(userAgent)) return { kind: "ios-browser" };
    // Safari and its look-alikes carry "Version/"; an app's own web view
    // (the Google app, TikTok, LinkedIn) does not.
    if (!/\bVersion\//.test(userAgent)) return { kind: "in-app", os, app: null };
    return { kind: "safari" };
  }
  // Android's WebView, inside another app, marks itself "; wv)".
  if (/; wv\)/.test(userAgent)) return { kind: "in-app", os, app: null };
  if (installPrompt) return { kind: "install" };
  return {
    kind: "android-menu",
    chrome:
      /\bChrome\/\d/.test(userAgent) && !/\b(SamsungBrowser|EdgA|OPR|YaBrowser)\//.test(userAgent),
  };
}
